import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Cohort, StudentSummary } from '@starter/shared';
import { useProgramId } from '../../auth/AuthContext.js';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { Modal } from '../../components/Modal.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

type FormMode = 'create' | 'enroll' | null;

export function StudentsPage() {
  const programId = useProgramId();
  const [students, setStudents] = useState<StudentSummary[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<FormMode>(null);
  const [enrollingStudent, setEnrollingStudent] = useState<StudentSummary | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [studentIdNumber, setStudentIdNumber] = useState('');
  const [cohortId, setCohortId] = useState('');
  const [enrollCohortId, setEnrollCohortId] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.listStudents({ programId, page: 1, pageSize: 100 }),
      api.listCohorts({ programId, page: 1, pageSize: 100 }),
    ])
      .then(([studentRes, cohortRes]) => {
        setStudents(studentRes.data);
        setCohorts(cohortRes.data);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load students'))
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(load, [load]);

  function openCreate() {
    setFullName('');
    setEmail('');
    setPassword('');
    setStudentIdNumber('');
    setCohortId('');
    setFormError(null);
    setMode('create');
  }

  function openEnroll(student: StudentSummary) {
    setEnrollingStudent(student);
    setEnrollCohortId('');
    setFormError(null);
    setMode('enroll');
  }

  function closeModal() {
    setMode(null);
    setEnrollingStudent(null);
    setFormError(null);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await api.createStudent({
        programId,
        fullName,
        email,
        password,
        studentIdNumber: studentIdNumber || null,
        cohortId: cohortId || undefined,
      });
      closeModal();
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleEnroll(e: React.FormEvent) {
    e.preventDefault();
    if (!enrollingStudent) return;
    setSaving(true);
    setFormError(null);
    try {
      await api.enrollStudent(enrollingStudent.id, { cohortId: enrollCohortId, status: 'planned' });
      closeModal();
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Enrollment failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Students"
        subtitle="Provision accounts and manage enrollments"
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            Add student
          </button>
        }
      />

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <LoadingState message="Loading students…" />
      ) : (
        <div className="card">
          {students.length === 0 ? (
            <p className="text-muted">No students yet. Add one to get started.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Student ID</th>
                    <th>Cohort</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <Link to={`/pd/students/${s.id}/documents`}>{s.fullName}</Link>
                      </td>
                      <td>{s.email}</td>
                      <td>{s.studentIdNumber ?? '—'}</td>
                      <td>{s.cohortName ?? '—'}</td>
                      <td>
                        {s.enrollmentStatus ? (
                          <StatusBadge status={s.enrollmentStatus} />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => openEnroll(s)}
                        >
                          Enroll
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        open={mode === 'create'}
        title="Add student"
        onClose={closeModal}
        footer={
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" form="student-form" className="btn btn--primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create'}
            </button>
          </div>
        }
      >
        <form id="student-form" onSubmit={handleCreate}>
          {formError && <div className="banner banner--error" role="alert">{formError}</div>}
          <label className="field">
            <span className="field__label">Full name</span>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Temporary password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </label>
          <label className="field">
            <span className="field__label">Student ID (optional)</span>
            <input value={studentIdNumber} onChange={(e) => setStudentIdNumber(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Initial cohort (optional)</span>
            <select value={cohortId} onChange={(e) => setCohortId(e.target.value)}>
              <option value="">None</option>
              {cohorts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </form>
      </Modal>

      <Modal
        open={mode === 'enroll'}
        title={`Enroll ${enrollingStudent?.fullName ?? 'student'}`}
        onClose={closeModal}
        footer={
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" form="enroll-form" className="btn btn--primary" disabled={saving}>
              {saving ? 'Enrolling…' : 'Enroll'}
            </button>
          </div>
        }
      >
        <form id="enroll-form" onSubmit={handleEnroll}>
          {formError && <div className="banner banner--error" role="alert">{formError}</div>}
          <label className="field">
            <span className="field__label">Cohort</span>
            <select value={enrollCohortId} onChange={(e) => setEnrollCohortId(e.target.value)} required>
              <option value="">Select cohort…</option>
              {cohorts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </label>
        </form>
      </Modal>
    </div>
  );
}
